/**
 * TenantServiceHistoryTab — resident/tenant service history.
 * Backed by GET /api/resident/history (entries[] shape): pickups, violations
 * and notifications for the account's unit, filterable by category + dates.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiSafeFetch } from '../lib/apiFetch';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CalendarClock, Filter, RefreshCcw } from 'lucide-react';

const STATUS_STYLE = {
  completed: 'bg-green-100 text-green-800 border-green-200',
  resolved:  'bg-green-100 text-green-800 border-green-200',
  requested: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  submitted: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  reviewed:  'bg-blue-100 text-blue-800 border-blue-200',
  in_progress: 'bg-purple-100 text-purple-800 border-purple-200',
  unread: 'bg-red-100 text-red-800 border-red-200',
  read: 'bg-zinc-100 text-zinc-700 border-zinc-200',
};
const CATS = [['all', 'All'], ['pickups', 'Pickups'], ['violations', 'Violations'], ['notifications', 'Notifications']];

export default function TenantServiceHistoryTab() {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [category, setCategory] = useState('all');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ category });
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    try {
      const { data: json } = await apiSafeFetch(`/api/resident/history?${params.toString()}`);
      if (json) setEntries(json.entries || []);
    } catch { /* swallow */ }
    setLoading(false);
  }, [dateFrom, dateTo, category]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="tenant-service-history-tab">
      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-[#C9A962]" />
            <div>
              <h3 className="text-lg font-bold" data-testid="tenant-service-history-heading">Service History</h3>
              <p className="text-xs text-zinc-500">Pickups, violation reports and notifications for your unit — newest first.</p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={load} className="text-xs" data-testid="tenant-service-history-refresh">
            <RefreshCcw className="h-3.5 w-3.5 mr-1" /> Refresh
          </Button>
        </div>

        <div className="grid sm:grid-cols-3 gap-3 mb-4" data-testid="tenant-service-history-filters">
          <div>
            <label className="block text-xs uppercase tracking-widest text-zinc-600 font-semibold mb-1">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="text-sm h-10" data-testid="tenant-service-history-date-from" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-zinc-600 font-semibold mb-1">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="text-sm h-10" data-testid="tenant-service-history-date-to" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-zinc-600 font-semibold mb-1">Category</label>
            {/* Iter-169 · Phase 2 — pill-tab category picker so all
                options are always visible, readable, and never clipped
                inside a select dropdown. */}
            <div className="flex flex-wrap gap-1.5" role="tablist"
                 data-testid="tenant-service-history-type-filter">
              {CATS.map(([k, l]) => {
                const active = category === k;
                return (
                  <button
                    key={k}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setCategory(k)}
                    className="px-3 py-2 rounded text-sm font-semibold border transition-colors"
                    style={{
                      backgroundColor: active ? '#C9A962' : '#ffffff',
                      color: active ? '#111827' : '#374151',
                      borderColor: active ? '#C9A962' : '#d1d5db',
                    }}
                    data-testid={`tenant-service-history-cat-${k}`}
                  >
                    {l}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {loading ? (
          <p className="text-center text-sm text-zinc-500 py-8" data-testid="tenant-service-history-loading">Loading service history…</p>
        ) : entries.length === 0 ? (
          <div className="text-center py-10" data-testid="tenant-service-history-empty">
            <Filter className="h-8 w-8 mx-auto text-zinc-300 mb-2" />
            <p className="text-sm text-zinc-600">No activity yet for the selected filters.</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-200" data-testid="tenant-service-history-list">
            {entries.map((ev, i) => {
              const dt = new Date(ev.date);
              return (
                <div key={i} className="py-4 flex items-start gap-3" data-testid="tenant-service-history-row">
                  <div className="flex-shrink-0 w-24 text-right">
                    <p className="text-sm font-bold text-zinc-900">{dt.toLocaleDateString()}</p>
                    <p className="text-xs text-zinc-600">{dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h4 className="text-base font-bold text-zinc-900 leading-snug">{ev.title}</h4>
                      {ev.status && (
                        <Badge className={STATUS_STYLE[ev.status] || 'bg-zinc-100 text-zinc-800'}>
                          {String(ev.status).replace('_', ' ')}
                        </Badge>
                      )}
                    </div>
                    {ev.detail && <p className="text-sm text-zinc-700 leading-snug break-words">{ev.detail}</p>}
                    {ev.notes && <p className="text-sm text-emerald-700 mt-1"><span className="font-semibold">Staff notes:</span> {ev.notes}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
